import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, DollarSign } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { financialAPI } from '../services/api';

const COLORS = ['#60a5fa', '#a78bfa', '#f472b6', '#34d399'];

const FinancialDashboard = ({ isAdmin }) => {
  const [financialData, setFinancialData] = useState(null);

  const loadFinancialData = useCallback(async () => {
    try {
      const response = await financialAPI.get();
      // Si les données sont un tableau vide ou invalide, utiliser des données par défaut
      if (!response.data || (Array.isArray(response.data) && response.data.length === 0)) {
        setFinancialData({
          revenue: [
            { month: 'Jan', value: 0 },
            { month: 'Fév', value: 0 },
            { month: 'Mar', value: 0 },
          ],
          expenses: {
            'R&D': 0,
            'Marketing': 0,
            'Opérations': 0,
          },
          profit_margin: 0
        });
      } else {
        setFinancialData(response.data);
      }
    } catch (error) {
      console.error('Error loading financial data:', error);
      // Données par défaut en cas d'erreur
      setFinancialData({
        revenue: [
          { month: 'Jan', value: 0 },
          { month: 'Fév', value: 0 },
          { month: 'Mar', value: 0 },
        ],
        expenses: {
          'R&D': 0,
          'Marketing': 0,
          'Opérations': 0,
        },
        profit_margin: 0
      });
    }
  }, []);

  useEffect(() => {
    loadFinancialData();
  }, [loadFinancialData]);

  if (!financialData) {
    return (
      <div className="glass rounded-2xl p-6 shadow-xl border border-slate-700/50 animate-pulse">
        <div className="h-64 bg-slate-700/30 rounded-lg" />
      </div>
    );
  }

  const expensesData = financialData.expenses ? Object.entries(financialData.expenses).map(([name, value]) => ({
    name,
    value,
  })) : [];

  return (
    <div className="glass rounded-2xl p-6 shadow-xl border border-slate-700/50 animate-slideIn">
      <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
        <TrendingUp className="w-6 h-6 text-green-400" />
        Tableau de Bord Financier
      </h2>

      {/* Revenue Chart */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-300 mb-4">Revenus Mensuels</h3>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={financialData.revenue || []}>
            <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
            <XAxis dataKey="month" stroke="#94a3b8" />
            <YAxis stroke="#94a3b8" />
            <Tooltip
              contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569' }}
              labelStyle={{ color: '#e2e8f0' }}
            />
            <Line type="monotone" dataKey="value" stroke="#60a5fa" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Expenses Chart */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-300 mb-4">Dépenses par Catégorie</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={expensesData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {expensesData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569' }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Profit Margin */}
        <div className="flex flex-col justify-center items-center bg-slate-700/30 rounded-xl p-6">
          <DollarSign className="w-12 h-12 text-green-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-300 mb-2">Marge Bénéficiaire</h3>
          <p className="text-4xl font-bold text-green-400" data-testid="profit-margin">
            {financialData.profit_margin || 0}%
          </p>
        </div>
      </div>
    </div>
  );
};

export default FinancialDashboard;